import { useEffect, useState, type PropsWithChildren } from 'react'
import { View, type ViewProps, type ScrollViewProps } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  type AnimatedScrollViewProps,
} from 'react-native-reanimated'
import { useScrollContext } from '~/components/layout/ScrollContext'
import { cn } from '~/lib/utils'

type CustomScrollViewProps = {
  indicatorClassName?: string
  offsetTrackHeight?: boolean
}

export function useScrollView({
  indicatorClassName,
  offsetTrackHeight = true,
}: CustomScrollViewProps = {}) {
  const { scrollY, maxScrollY, setMaxScrollY } = useScrollContext()

  const layoutHeight = useSharedValue(0)
  const showIndicator = useSharedValue(false)

  const scrollHandler = useAnimatedScrollHandler(
    {
      onScroll: (event) => {
        scrollY.set(event.contentOffset.y)
        layoutHeight.value = event.layoutMeasurement.height

        showIndicator.value = event.contentSize.height > event.layoutMeasurement.height

        const maxScrollY = event.contentSize.height - event.layoutMeasurement.height

        setMaxScrollY(maxScrollY)
      },
    },
    [scrollY, setMaxScrollY]
  )

  const indicatorStyle = useAnimatedStyle(() => {
    if (!showIndicator.value || layoutHeight.value === 0) {
      return {
        opacity: 0,
      }
    }

    const trackHeight = offsetTrackHeight
      ? layoutHeight.value - 60 // 30px padding top + bottom for the 50px indicator
      : layoutHeight.value

    const indicatorTop = interpolate(
      scrollY.value,
      [0, maxScrollY],
      offsetTrackHeight ? [30, trackHeight - 70] : [0, trackHeight],
      Extrapolation.CLAMP
    )

    return {
      opacity: 1,
      transform: [{ translateY: indicatorTop }],
    }
  })

  const scrollIndicator = (
    <Animated.View
      className={cn(
        'pointer-events-auto absolute right-0 top-0 w-2 cursor-pointer rounded-full bg-secondary',
        'sm:-right-2',
        indicatorClassName
      )}
      style={[
        {
          height: 100,
          zIndex: 1000,
        },
        indicatorStyle,
      ]}
      // eslint-disable-next-line i18next/no-literal-string
      pointerEvents="auto"
    />
  )

  const props = {
    showsVerticalScrollIndicator: false,
    onScroll: scrollHandler,
  }

  return { scrollIndicator, props }
}

export function useStickyFooter({
  content,
  footerClassName,
}: {
  content?: React.ReactNode
  footerClassName?: string
} = {}) {
  const [footerHeight, setFooterHeight] = useState(0)
  const [scrollViewHeight, setScrollViewHeight] = useState(0)
  const [wrapperHeight, setWrapperHeight] = useState(0)
  const [wrapperY, setWrapperY] = useState(0)

  // Calculate the bottom inset for the footer
  // When content is shorter than the wrapper, keep footer at bottom of viewport
  // When content overflows, let footer scroll with content
  const contentOverflows = scrollViewHeight > wrapperHeight
  const footerBottomInset =
    wrapperHeight > 0 && wrapperY >= 0
      ? contentOverflows
        ? Math.max(0, wrapperY) // Content overflows: use wrapperY offset
        : Math.max(0, wrapperY + (wrapperHeight - scrollViewHeight)) // Short content: adjust to keep footer visible
      : 0

  const Footer = content ? (
    <View
      className={cn('absolute w-full', footerClassName)}
      // Use pixel number for RN style; on web, add the safe-area bottom via CSS var padding on the wrapper
      style={{ bottom: footerBottomInset }}
      onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}>
      {content}
    </View>
  ) : null

  const scrollViewProps = {
    onContentSizeChange: (_, height) => setScrollViewHeight(height),
    onLayout: (e) => {
      setWrapperHeight(e.nativeEvent.layout.height)
      setWrapperY(e.nativeEvent.layout.y)
    },
  } satisfies Pick<ScrollViewProps, 'onContentSizeChange' | 'onLayout'>

  return {
    element: Footer,
    scrollViewProps,
    footerHeight,
  }
}

export function ScrollView({
  children,
  indicatorClassName,
  footerClassName,
  footer,
  offsetTrackHeight = false,
  hideFooterPlaceholder = false,
  ref,
  ...props
}: {
  indicatorClassName?: string
  offsetTrackHeight?: boolean
  footerClassName?: string
  footer?: React.ReactNode
  hideFooterPlaceholder?: boolean
} & PropsWithChildren<ViewProps & AnimatedScrollViewProps>) {
  const { scrollY } = useScrollContext()

  const { scrollIndicator, props: scrollProps } = useScrollView({
    indicatorClassName,
    offsetTrackHeight,
  })

  const {
    element: footerElement,
    scrollViewProps: scrollViewFooterProps,
    footerHeight,
  } = useStickyFooter({
    footerClassName,
    content: footer,
  })

  useEffect(
    () => () => {
      scrollY.set(0)
    },
    [scrollY]
  )

  return (
    <>
      <Animated.ScrollView
        ref={ref}
        {...props}
        {...(footer && scrollViewFooterProps)}
        {...scrollProps}>
        {children}
        {footer && !hideFooterPlaceholder && <View style={{ height: footerHeight }} />}
      </Animated.ScrollView>
      {footer && footerElement}
      {scrollIndicator}
    </>
  )
}
